import * as React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { QueryParamConfigMap, useCustomSearchParams } from 'common/hooks/customSearchParams';
import { DEFAULT_DATE_RANGE_PARAM, WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import _ from 'lodash';
import { FilterButton } from '../../../../../components/ContainerFilters/FilterButton';
import { NestedFilter } from '../../../../../components/ContainerFilters/NestedFilter';
import { ClosedAreaFilter } from './ClosedAreaFilter';
import { DemurrageDueFilter } from './DemurrageDueFilter';
import { PickupAppointmentScheduledFilter } from './PickupAppointmentScheduledFilter';
import { GatedOutDateFilter } from './GatedOutDateFilter';
import { GatedInDateFilter } from './GatedInDateFilter';
import { MilestoneFilterKey } from './utils';
import { ShippingLineFilter } from './ShippingLineFilter';
import { ContainerTypeFilter } from './ContainerTypeFilter';
import { GatedOutFilterKind, getGatedOutFilters } from './parsers/gated-out';

export function getCountForKey(searchParams: QueryParamConfigMap, key: string): number {
  const v = searchParams[key];
  if (
    _.isNil(v) ||
    (_.isArray(v) && v.length === 0) ||
    (!_.isArray(v) && _.isObject(v) && _.values(v).every(_.isEmpty))
  ) {
    return 0;
  }

  if (!_.isArray(v) && _.isObject(v)) {
    // Conflict with Gated out 5+ days
    if (key === 'gatedOutAt') {
      return _.values(v).filter((f: unknown) => f && f !== '-5:days').length;
    }
    return _.values(v).filter((f) => !_.isEmpty(f)).length;
  }
  if (_.isArray(v) && !_.isEmpty(v)) {
    return 1;
  }

  return 1;
}

const nestedFilterKeys: MilestoneFilterKey[] = [
  'watchedAt',
  'closedArea',
  'demurrageDue',
  'pickupAppointmentAt',
  'returnAppointmentStatus',
  'gatedInAt',
  'shippingLine',
  'containerType',
];

export const MoreFilters: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS, true);

  const controlLabelClasses = {
    label: 'font-medium text-sm',
    root: 'm-0 py-1 hover:bg-blue-200 rounded-sm',
  };

  const count = React.useMemo(() => {
    let total = 0;
    nestedFilterKeys.forEach((k) => {
      total += getCountForKey(searchParams, k);
    });
    // Count status filter as 1
    if (
      getCountForKey(searchParams, 'pickupAppointmentAt') === 0 &&
      getCountForKey(searchParams, 'pickupAppointmentStatus') > 0
    ) {
      total += 1;
    }

    // Counts for gatedOut
    total += getGatedOutFilters(searchParams).filter((f) =>
      [GatedOutFilterKind.Today, GatedOutFilterKind.Yesterday].includes(f.kind)
    ).length;

    return total;
  }, [searchParams]);

  const clearFilters = () => {
    const resetFilters: typeof searchParams = {};
    nestedFilterKeys.forEach((k) => {
      resetFilters[k] = null;
    });
    resetFilters.pickupAppointmentStatus = null;
    setSearchParams(resetFilters);
  };

  return (
    <FilterButton
      title="More filters"
      count={count}
      clearFilter={clearFilters}
      isOpen={open}
      onRequestOpen={setOpen}
    >
      <>
        <FormGroup className="px-2 w-72">
          <FormControlLabel
            classes={controlLabelClasses}
            control={
              <Checkbox
                size="small"
                checked={searchParams.watchedAt.startDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchParams({
                    watchedAt: {
                      ...DEFAULT_DATE_RANGE_PARAM,
                      startDate: e.target.checked ? '-3:days' : null,
                    },
                  });
                }}
              />
            }
            label="Recently added (last 3 days)"
          />
        </FormGroup>
        <NestedFilter label="Closed area" isActive={getCountForKey(searchParams, 'closedArea') > 0}>
          <ClosedAreaFilter />
        </NestedFilter>
        <NestedFilter label="Demurrage" isActive={getCountForKey(searchParams, 'demurrageDue') > 0}>
          <DemurrageDueFilter />
        </NestedFilter>
        <NestedFilter
          label="Pickup appointment scheduled"
          isActive={
            getCountForKey(searchParams, 'pickupAppointmentAt') > 0 ||
            getCountForKey(searchParams, 'pickupAppointmentStatus') > 0
          }
        >
          <PickupAppointmentScheduledFilter />
        </NestedFilter>
        <NestedFilter
          label="Gated out date"
          isActive={
            getGatedOutFilters(searchParams).filter((f) =>
              [GatedOutFilterKind.Today, GatedOutFilterKind.Yesterday].includes(f.kind)
            ).length > 0
          }
        >
          <GatedOutDateFilter />
        </NestedFilter>
        <NestedFilter label="Terminated" isActive={getCountForKey(searchParams, 'gatedInAt') > 0}>
          <GatedInDateFilter />
        </NestedFilter>
        <NestedFilter
          label="Steamship Line"
          className="w-72"
          isActive={getCountForKey(searchParams, 'shippingLine') > 0}
        >
          <ShippingLineFilter />
        </NestedFilter>
        <NestedFilter
          label="Container Type"
          className="w-72"
          isActive={getCountForKey(searchParams, 'containerType') > 0}
        >
          <ContainerTypeFilter />
        </NestedFilter>
      </>
    </FilterButton>
  );
};
MoreFilters.displayName = 'TrackAndTrace.Controls.filters.MoreFilters';
